import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  User, FileText, ClipboardCheck, Briefcase, Calendar, Mail, Phone, MapPin,
  AlertTriangle, CheckCircle2, Circle, Upload, Save, ArrowUpRight, Lock,
  ShieldCheck, Award,
} from "lucide-react";
import FileUpload from "@/components/FileUpload";

const TABS = [
  { id: "overview",   label: "Overview",    icon: User },
  { id: "profile",    label: "Profile",     icon: User },
  { id: "folder",     label: "HR folder",   icon: FileText },
  { id: "onboarding", label: "Onboarding",  icon: ClipboardCheck },
  { id: "tasks",      label: "My tasks",    icon: Briefcase },
];

/** Employee Self-Service — `/portal/me`.
 *  Every authenticated member lands here to see themselves.
 *  Staff members with a linked employee record get their HR folder + onboarding.
 *  Elected/appointed members and pure community members see just profile + tasks.
 */
export default function Me() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [tasks, setTasks] = useState([]);

  const load = async () => {
    const [p, ob, t] = await Promise.all([
      api.get("/me/profile"),
      api.get("/me/onboarding").catch(() => ({ data: { exists: false } })),
      api.get("/me/tasks").catch(() => ({ data: { tasks: [] } })),
    ]);
    setProfile(p.data);
    setOnboarding(ob.data);
    setTasks(t.data.tasks || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!profile) return <div className="max-w-[1200px] mx-auto p-10 font-mono text-xs text-white/40">Loading your workspace…</div>;

  const emp = profile.employee;
  const isStaff = profile.has_employee_record;
  const hasOnboarding = onboarding?.exists;
  const roleLabel = user?.role?.replace(/_/g, " ") || "member";
  const displayName = emp?.preferred_name || user?.first_name;

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10">
      {/* Hero header */}
      <header className="mb-8">
        <div className="label-mono" style={{ color: "#89b4fa" }}>::: Your workspace</div>
        <div className="flex items-baseline justify-between gap-4 flex-wrap mt-2">
          <h1 className="font-display text-4xl">
            Miigwech, <span style={{ color: "#B0BEC5" }}>{displayName || "friend"}</span>
          </h1>
          <div className="font-mono text-xs text-white/60 flex items-center gap-3">
            <span className="tag" style={{ color: "#89b4fa", borderColor: "rgba(137,180,250,0.4)" }}>{roleLabel}</span>
            {emp?.department && (
              <span className="tag" style={{ color: "#B0BEC5", borderColor: "rgba(176,190,197,0.35)" }}>
                {emp.department}
              </span>
            )}
            {user?.is_demo && (
              <span className="tag" style={{ color: "#d4b876", borderColor: "rgba(212,184,118,0.5)" }}>demo</span>
            )}
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="border-b border-white/10 mb-8 flex gap-1 flex-wrap" data-testid="me-tabs">
        {TABS.filter(t => (t.id !== "folder" && t.id !== "onboarding") || isStaff).map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`me-tab-${t.id}`}
              className={`px-4 py-2.5 font-mono text-xs flex items-center gap-2 border-b-2 transition-colors ${
                active ? "text-white" : "text-white/50 hover:text-white/80 border-transparent"
              }`}
              style={{ borderBottomColor: active ? "#89b4fa" : "transparent" }}
            >
              <Icon size={12} /> {t.label}
              {t.id === "onboarding" && hasOnboarding && onboarding.progress?.percent < 100 && (
                <span className="ml-1 font-mono text-[9px] px-1.5 py-0.5" style={{ background: "rgba(212,184,118,0.15)", color: "#d4b876" }}>
                  {onboarding.progress.percent}%
                </span>
              )}
              {t.id === "tasks" && tasks.length > 0 && (
                <span className="ml-1 font-mono text-[9px] px-1.5 py-0.5" style={{ background: "rgba(137,180,250,0.15)", color: "#89b4fa" }}>
                  {tasks.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "overview"   && <OverviewPane profile={profile} onboarding={onboarding} tasks={tasks} setTab={setTab} />}
      {tab === "profile"    && <ProfilePane profile={profile} onSaved={() => { load(); refresh(); }} />}
      {tab === "folder"     && isStaff && <FolderPane employee={emp} onSaved={load} />}
      {tab === "onboarding" && isStaff && <OnboardingPane onboarding={onboarding} onChanged={load} />}
      {tab === "tasks"      && <TasksPane tasks={tasks} />}
    </div>
  );
}

/* ============================ OVERVIEW ============================ */

function OverviewPane({ profile, onboarding, tasks, setTab }) {
  const emp = profile.employee;
  const isStaff = profile.has_employee_record;
  const hasOnb = onboarding?.exists;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {/* Left col — Snapshot */}
      <div className="md:col-span-2 space-y-5">
        <div className="panel p-6 corner-tick" data-testid="overview-snapshot">
          <div className="label-mono flex items-center gap-2 mb-4" style={{ color: "#89b4fa" }}>
            <User size={12} /> Personal snapshot
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <SnapRow icon={Mail}      label="Work email"      value={emp?.work_email || profile.user.email}/>
            <SnapRow icon={Mail}      label="Personal email"  value={emp?.personal_email || profile.user.personal_email}/>
            <SnapRow icon={Phone}     label="Phone"           value={emp?.phone || profile.user.phone}/>
            <SnapRow icon={MapPin}    label="Address"         value={emp?.address || profile.user.address}/>
            {isStaff && (
              <>
                <SnapRow icon={Briefcase} label="Position"      value={emp.position_title}/>
                <SnapRow icon={Calendar}  label="Hire date"     value={emp.hire_date}/>
                <SnapRow icon={ShieldCheck} label="Status"      value={emp.status?.replace(/_/g," ")}/>
                <SnapRow icon={Award}     label="Employment"    value={emp.employment_type?.replace(/_/g," ")}/>
              </>
            )}
          </div>
          <button onClick={() => setTab("profile")} className="btn-cta mt-5" data-testid="jump-to-profile">
            <User size={12}/> Edit personal info
          </button>
        </div>

        {/* Emergency contact */}
        {isStaff && (
          <div className="panel p-6 corner-tick">
            <div className="label-mono flex items-center gap-2 mb-4" style={{ color: "#ef5b6b" }}>
              <AlertTriangle size={12} /> Emergency contact
            </div>
            {emp.emergency_contact_name || emp.emergency_contact_phone ? (
              <div className="text-sm space-y-1">
                <div className="font-display text-lg">{emp.emergency_contact_name || "—"}</div>
                <div className="font-mono text-xs text-white/60">{emp.emergency_contact_phone || "—"}</div>
              </div>
            ) : (
              <div className="font-mono text-xs text-white/40">
                No emergency contact on file. Please add one from the Profile tab — this is critical for workplace safety.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right col — Progress rings */}
      <div className="space-y-5">
        {hasOnb && onboarding.progress.percent < 100 && (
          <div className="panel p-6 corner-tick" data-testid="overview-onboarding-widget">
            <div className="label-mono flex items-center gap-2 mb-3" style={{ color: "#d4b876" }}>
              <ClipboardCheck size={12} /> Onboarding
            </div>
            <div className="font-display text-4xl tabular-nums" style={{ color: "#d4b876" }}>
              {onboarding.progress.percent}%
            </div>
            <div className="h-1.5 bg-white/5 mt-3">
              <div className="h-full" style={{ width: `${onboarding.progress.percent}%`, background: "#d4b876" }} />
            </div>
            <div className="font-mono text-[10px] text-white/50 mt-2">
              {onboarding.progress.completed}/{onboarding.progress.total} items complete
            </div>
            <button onClick={() => setTab("onboarding")} className="btn-cta gold mt-4 w-full" data-testid="jump-to-onboarding">
              <ArrowUpRight size={12}/> Continue onboarding
            </button>
          </div>
        )}

        <div className="panel p-6 corner-tick" data-testid="overview-tasks-widget">
          <div className="label-mono flex items-center gap-2 mb-3" style={{ color: "#89b4fa" }}>
            <Briefcase size={12} /> Open tasks
          </div>
          <div className="font-display text-4xl tabular-nums">{tasks.length}</div>
          <div className="font-mono text-[10px] text-white/50 mt-2">
            {tasks.filter(t => t.overdue).length > 0 ? `${tasks.filter(t => t.overdue).length} past-deadline` : "all on schedule"}
          </div>
          <button onClick={() => setTab("tasks")} className="btn-cta primary mt-4 w-full" data-testid="jump-to-tasks">
            <ArrowUpRight size={12}/> View my tasks
          </button>
        </div>

        {isStaff && (
          <div className="panel p-6 corner-tick">
            <div className="label-mono flex items-center gap-2 mb-3" style={{ color: "#B0BEC5" }}>
              <FileText size={12} /> HR folder
            </div>
            <div className="font-display text-4xl tabular-nums">{emp.documents?.length || 0}</div>
            <div className="font-mono text-[10px] text-white/50 mt-2">documents on file</div>
            <button onClick={() => setTab("folder")} className="btn-cta mt-4 w-full" data-testid="jump-to-folder">
              <ArrowUpRight size={12}/> Open folder
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SnapRow({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="label-mono text-white/50 flex items-center gap-1.5 mb-1"><Icon size={11}/> {label}</div>
      <div className="font-mono text-xs text-white/85 truncate" title={value || "—"}>{value || <span className="text-white/30">not set</span>}</div>
    </div>
  );
}

/* ============================ PROFILE ============================ */

function ProfilePane({ profile, onSaved }) {
  const emp = profile.employee;
  const [f, setF] = useState({
    preferred_name: emp?.preferred_name || "",
    phone: emp?.phone || profile.user.phone || "",
    personal_email: emp?.personal_email || profile.user.personal_email || "",
    address: emp?.address || profile.user.address || "",
    emergency_contact_name: emp?.emergency_contact_name || "",
    emergency_contact_phone: emp?.emergency_contact_phone || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me/profile", f);
      toast.success("Profile updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="panel p-6 space-y-5 max-w-3xl" data-testid="profile-form">
      <div className="label-mono flex items-center gap-2" style={{ color: "#89b4fa" }}>
        <User size={12}/> Personal information
      </div>
      <p className="font-mono text-xs text-white/50">
        Only you can edit this. Legal name, role, department and pay information stay under HR control.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Preferred name">
          <input value={f.preferred_name} onChange={e=>setF({...f, preferred_name:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="profile-preferred-name"/>
        </Field>
        <Field label="Phone">
          <input value={f.phone} onChange={e=>setF({...f, phone:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="profile-phone"/>
        </Field>
        <Field label="Personal email">
          <input type="email" value={f.personal_email} onChange={e=>setF({...f, personal_email:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="profile-personal-email"/>
        </Field>
        <Field label="Address">
          <input value={f.address} onChange={e=>setF({...f, address:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="profile-address"/>
        </Field>
      </div>

      <div className="pt-4 border-t border-white/10">
        <div className="label-mono flex items-center gap-2 mb-3" style={{ color: "#ef5b6b" }}>
          <AlertTriangle size={12}/> Emergency contact
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Contact name">
            <input value={f.emergency_contact_name} onChange={e=>setF({...f, emergency_contact_name:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="profile-emergency-name"/>
          </Field>
          <Field label="Contact phone">
            <input value={f.emergency_contact_phone} onChange={e=>setF({...f, emergency_contact_phone:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="profile-emergency-phone"/>
          </Field>
        </div>
      </div>

      <div className="flex justify-end pt-3">
        <button type="submit" disabled={saving} className="btn-cta primary" data-testid="profile-save-btn">
          <Save size={12}/> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-mono text-white/60 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

/* ============================ HR FOLDER ============================ */

function FolderPane({ employee, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [newDoc, setNewDoc] = useState({ label: "", kind: "other", url: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!newDoc.url || !newDoc.label) { toast.error("Add a label and upload a file"); return; }
    setUploading(true);
    try {
      await api.post("/me/employee-folder/documents", newDoc);
      toast.success("Document added to your folder");
      setNewDoc({ label: "", kind: "other", url: "" });
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="panel p-5 flex items-center gap-3">
        <Lock size={14} className="text-white/50"/>
        <div className="font-mono text-xs text-white/70">
          Your HR folder is private — only you and HR administrators can see these documents.
          SIN, tax details and pay information are always masked.
        </div>
      </div>

      {/* Existing documents */}
      <div>
        <div className="label-mono mb-3" style={{ color: "#B0BEC5" }}>::: On file · {employee.documents?.length || 0} documents</div>
        {employee.documents?.length ? (
          <div className="space-y-2" data-testid="folder-doc-list">
            {employee.documents.map(d => (
              <div key={d.id} className="border border-white/10 p-4 flex items-center justify-between gap-3 hover:border-white/25 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={14} className="text-white/50 shrink-0"/>
                  <div className="min-w-0">
                    <div className="font-display text-sm truncate">{d.label}</div>
                    <div className="font-mono text-[10px] text-white/50 truncate">
                      {d.kind?.replace(/_/g," ")} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ""}
                      {d.self_uploaded && <span className="ml-2 text-white/40">· self-uploaded</span>}
                    </div>
                  </div>
                </div>
                {d.url && (
                  <a href={d.url} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white p-1" title="Open">
                    <ArrowUpRight size={14}/>
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="panel p-8 text-center corner-tick">
            <div className="font-mono text-xs text-white/40">Your folder is empty — HR or you can upload documents below.</div>
          </div>
        )}
      </div>

      {/* Upload new document */}
      <form onSubmit={submit} className="panel p-6 space-y-4" data-testid="folder-upload-form">
        <div className="label-mono flex items-center gap-2" style={{ color: "#89b4fa" }}>
          <Upload size={12}/> Add a document
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Label">
            <input required value={newDoc.label} onChange={e=>setNewDoc({...newDoc, label:e.target.value})} placeholder="e.g. Direct deposit void cheque" className="w-full px-3 py-2 font-mono text-sm" data-testid="folder-doc-label"/>
          </Field>
          <Field label="Type">
            <select value={newDoc.kind} onChange={e=>setNewDoc({...newDoc, kind:e.target.value})} className="w-full px-3 py-2 font-mono text-sm bg-transparent border border-white/20" data-testid="folder-doc-kind">
              <option value="td1_federal">TD1 (Federal)</option>
              <option value="td1_provincial">TD1 (Provincial)</option>
              <option value="void_cheque">Void cheque / direct deposit</option>
              <option value="policy_ack">Policy acknowledgment</option>
              <option value="confidentiality">Confidentiality agreement</option>
              <option value="health_card">Health card</option>
              <option value="other">Other</option>
            </select>
          </Field>
        </div>
        <FileUpload
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          maxSizeMb={10}
          value={newDoc.url ? { id: newDoc._fileId, original_filename: newDoc._name } : null}
          onChange={(res) => {
            if (res?.file) {
              setNewDoc({
                ...newDoc,
                url: `/api/files/${res.file.id}`,
                _fileId: res.file.id,
                _name: res.file.original_filename,
              });
            } else {
              setNewDoc({ ...newDoc, url: "", _fileId: undefined, _name: undefined });
            }
          }}
          label="Upload file (PDF or image, max 10MB)"
          testid="folder-doc-file"
        />
        <div className="flex justify-end">
          <button type="submit" disabled={uploading || !newDoc.url} className="btn-cta primary" data-testid="folder-upload-btn">
            <Upload size={12}/> {uploading ? "Adding…" : "Add to my folder"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ============================ ONBOARDING ============================ */

function OnboardingPane({ onboarding, onChanged }) {
  const [busyItem, setBusyItem] = useState(null);
  const pkg = onboarding?.package;
  if (!pkg) {
    return (
      <div className="panel p-10 text-center corner-tick">
        <ClipboardCheck size={20} className="mx-auto text-white/40 mb-3"/>
        <div className="font-display text-lg mb-1">No onboarding package active</div>
        <div className="font-mono text-xs text-white/50">
          You're either not new to KLFN or HR hasn't issued your checklist yet. This is normal.
        </div>
      </div>
    );
  }

  const toggle = async (itemId, completed) => {
    setBusyItem(itemId);
    try {
      await api.patch(`/me/onboarding/items/${itemId}`, { completed });
      toast.success(completed ? "Item complete — great work" : "Item unchecked");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusyItem(null); }
  };

  const attachDoc = async (itemId, url) => {
    try {
      await api.patch(`/me/onboarding/items/${itemId}`, { doc_url: url });
      toast.success("Document attached");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Attach failed"); }
  };

  const required = pkg.items.filter(i => i.required);
  const optional = pkg.items.filter(i => !i.required);
  const doneReq = required.filter(i => i.completed).length;

  return (
    <div className="space-y-6">
      {/* Progress ribbon */}
      <div className="panel p-6 corner-tick" data-testid="onboarding-progress">
        <div className="flex items-baseline justify-between mb-3">
          <div className="label-mono" style={{ color: "#d4b876" }}>::: Welcome to Kasabonika Lake</div>
          <span className="font-display text-3xl tabular-nums" style={{ color: "#d4b876" }}>
            {onboarding.progress.percent}%
          </span>
        </div>
        <div className="h-1.5 bg-white/5">
          <div className="h-full transition-all" style={{ width: `${onboarding.progress.percent}%`, background: "#d4b876" }} />
        </div>
        <div className="font-mono text-xs text-white/60 mt-3">
          {doneReq}/{required.length} required · {pkg.items.filter(i => i.completed).length}/{pkg.items.length} total
        </div>
        {pkg.issued_by_name && (
          <div className="font-mono text-[10px] text-white/40 mt-1">Issued by {pkg.issued_by_name}</div>
        )}
      </div>

      {/* Required items */}
      <div>
        <div className="label-mono mb-3" style={{ color: "#ef5b6b" }}>::: Required · Must complete before probation ends</div>
        <div className="space-y-2">
          {required.map(item => (
            <ItemRow key={item.id} item={item} busy={busyItem === item.id} onToggle={toggle} onAttach={attachDoc}/>
          ))}
        </div>
      </div>

      {optional.length > 0 && (
        <div>
          <div className="label-mono mb-3" style={{ color: "#B0BEC5" }}>::: Optional · recommended</div>
          <div className="space-y-2">
            {optional.map(item => (
              <ItemRow key={item.id} item={item} busy={busyItem === item.id} onToggle={toggle} onAttach={attachDoc}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemRow({ item, busy, onToggle, onAttach }) {
  return (
    <div
      className={`border p-4 flex items-start gap-4 transition-colors ${
        item.completed ? "border-[#a6e3a1]/30 bg-[#a6e3a1]/5" : "border-white/10 hover:border-white/25"
      }`}
      data-testid={`onboarding-item-${item.id}`}
    >
      <button
        onClick={() => onToggle(item.id, !item.completed)}
        disabled={busy}
        className="shrink-0 mt-0.5"
        title={item.completed ? "Mark incomplete" : "Mark complete"}
        data-testid={`onboarding-toggle-${item.id}`}
      >
        {item.completed ? (
          <CheckCircle2 size={18} style={{ color: "#a6e3a1" }} />
        ) : (
          <Circle size={18} className="text-white/40 hover:text-white/70"/>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`font-display text-sm ${item.completed ? "text-white/60" : ""}`}>{item.label}</div>
        {item.completed_at && (
          <div className="font-mono text-[10px] text-white/40 mt-0.5">
            Completed {new Date(item.completed_at).toLocaleString()}
          </div>
        )}
        {!item.completed && item.kind !== "portal_account" && item.kind !== "it_creds" && (
          <div className="mt-3">
            <FileUpload
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              maxSizeMb={10}
              onChange={(res) => res?.file && onAttach(item.id, `/api/files/${res.file.id}`)}
              label={item.doc_url ? "Replace attached file" : "Attach supporting document"}
            />
          </div>
        )}
        {item.doc_url && (
          <a href={item.doc_url} target="_blank" rel="noreferrer" className="font-mono text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1 mt-2">
            <FileText size={11}/> View attached
          </a>
        )}
      </div>
    </div>
  );
}

/* ============================ TASKS ============================ */

function TasksPane({ tasks }) {
  const grouped = useMemo(() => {
    const g = { docket: [], onboarding: [], application: [] };
    tasks.forEach(t => { (g[t.kind] || (g[t.kind] = [])).push(t); });
    return g;
  }, [tasks]);

  if (tasks.length === 0) {
    return (
      <div className="panel p-10 text-center corner-tick">
        <CheckCircle2 size={20} className="mx-auto mb-3" style={{ color: "#a6e3a1" }}/>
        <div className="font-display text-lg mb-1">You're all caught up</div>
        <div className="font-mono text-xs text-white/50">
          Nothing on your plate right now. When someone assigns you a docket, an onboarding checklist opens, or your application status changes, it will appear here.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-list">
      {grouped.docket?.length > 0 && <TaskGroup label="Dockets" tasks={grouped.docket} />}
      {grouped.onboarding?.length > 0 && <TaskGroup label="Onboarding" tasks={grouped.onboarding} />}
      {grouped.application?.length > 0 && <TaskGroup label="Applications" tasks={grouped.application} />}
    </div>
  );
}

function TaskGroup({ label, tasks }) {
  return (
    <div>
      <div className="label-mono mb-3" style={{ color: "#89b4fa" }}>::: {label}</div>
      <div className="space-y-2">
        {tasks.map(t => (
          <Link
            to={t.link || "#"}
            key={`${t.kind}-${t.id}`}
            className="border border-white/10 p-4 flex items-center justify-between gap-3 hover:border-white/30 transition-colors block"
            data-testid={`task-${t.kind}-${t.id}`}
          >
            <div className="min-w-0">
              <div className="font-display text-sm truncate">{t.title}</div>
              <div className="font-mono text-[10px] text-white/50 mt-1 flex items-center gap-2">
                {t.priority && <span>priority: {t.priority}</span>}
                {t.due && <span>due {new Date(t.due).toLocaleDateString()}</span>}
                {t.overdue && <span style={{ color: "#ef5b6b" }}>· overdue</span>}
                {t.meta && <span>{t.meta}</span>}
              </div>
              {typeof t.progress === "number" && (
                <div className="h-1 bg-white/5 mt-2 w-40">
                  <div className="h-full" style={{ width: `${t.progress}%`, background: t.overdue ? "#ef5b6b" : "#89b4fa" }}/>
                </div>
              )}
            </div>
            <ArrowUpRight size={14} className="text-white/40 shrink-0"/>
          </Link>
        ))}
      </div>
    </div>
  );
}
